import { submitAssignment } from "@/api/common";
import { DangerIcon } from "@/assets/icons";
import { useLayout } from "@/hooks/useLayout";
import { showSuccessToast } from "@/lib/toast";
import { cn, sanitizeHtmlOptions } from "@/lib/utils";
import { Assignment, AssignmentSubmission } from "@/types/assignments.types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAfter } from "date-fns";
import { Formik } from "formik";
import sanitize from "sanitize-html";
import * as Yup from "yup";
import CustomEditor from "./CustomEditor";
import Dropzone from "./Dropzone";
import { Button } from "./ui/button";
import { LessonAttachments } from "./Lessons";
import { MathJax } from "better-react-mathjax";
import { getAssignmentSubmission } from "@/api/students";
import useAuth from "@/hooks/useAuth";

type Props = {
  assignment: Assignment;
  closeModal?: () => void;
};

const AssignmentSubmitArea = ({ assignment, closeModal }: Props) => {
  const layout = useLayout();
  const { user } = useAuth()

  const { data } = useQuery({
    queryKey: ["submission", assignment?.id, user?.id],
    queryFn: () => getAssignmentSubmission({ assignment_id: assignment?.id, student_id: user?.id }),
    suspense: true
  })

  if (layout !== "student") return null;

  const submission = data?.data!

  return (
    <>
      <p className="ml-auto mt-10 font-semibold text-[#2C323C]">
        Grade: {submission?.grade ? `${submission?.grade}%` : "Not Graded Yet"}
      </p>
      <PreviousSubmission submission={submission} />
      <SubmitAssignmentForm assignment={assignment} submission={submission} closeModal={closeModal} />
    </>
  );
};

const PreviousSubmission = ({ submission }: { submission: AssignmentSubmission | null }) => {

  if (!submission) return null;

  return (
    <div className="mb-10">
      <p className="mb-2 page-header">Submission</p>
      <MathJax>
        <div
          className="mb-5"
          dangerouslySetInnerHTML={{
            __html: sanitize(submission.reply, sanitizeHtmlOptions),
          }}
        />
      </MathJax>
      <LessonAttachments
        attachments={submission.attachment}
        className={cn(!!submission.attachment && "hidden")}
      />
      {/* {!!submission.remark && (
        <>
          <p className="mt-8 mb-2 page-header">Remark</p>
          <MathJax>
            <div
              className="mb-5"
              dangerouslySetInnerHTML={{
                __html: sanitize(submission.remark, sanitizeHtmlOptions),
              }}
            />
          </MathJax>
        </>
      )} */}
    </div>
  );
};

const SubmitAssignmentForm = ({ assignment, closeModal, submission }: Props & { submission: AssignmentSubmission | null }) => {
  const { user } = useAuth()
  const queryClient = useQueryClient();

  const initialValues = {
    assignment_id: assignment.id,
    student_id: user?.id,
    response: "",
    doc: null,
  };
  const validationSchema = Yup.object().shape({
    response: Yup.string(),
  });

  const { mutate, isLoading } = useMutation(submitAssignment, {
    onSuccess: () => {
      showSuccessToast("Assignment submitted successfully");
      closeModal?.();
      queryClient.refetchQueries({ queryKey: ["lesson"] });
      queryClient.refetchQueries({ queryKey: ["submission"] });
    },
  });

  if (!!submission?.grade) return <GradedRender />;

  if (isAfter(new Date(), assignment.dueDate)) return <LateSubmissionRender />;

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values, { resetForm }) => mutate(values, { onSuccess: () => resetForm() })}
    >
      {({ handleSubmit, setFieldValue }) => (
        <form onSubmit={handleSubmit} className="grid gap-6 mt-6">
          <p className="page-header">Submit Assignment</p>
          <CustomEditor
            label="Assignment Answer(s)"
            setState={(editorState) => setFieldValue("response", editorState)}
          />
          <Dropzone name="doc" type="file" />

          {isAfter(new Date(), assignment.dueDate) && <LateSubmissionRender inner />}

          <div className="flex items-center gap-3 ml-auto">
            <Button type="button" variant="outline">
              Cancel
            </Button>
            <Button
              type="submit"
              className="min-w-[100px]"
              disabled={isLoading}
            >
              Submit
            </Button>
          </div>
        </form>
      )}
    </Formik>
  );
};

const LateSubmissionRender = ({ inner }: { inner?: boolean }) => {
  return (
    <div className="flex items-center gap-2 p-3 mt-6 text-red-500 rounded-lg bg-red-50">
      <DangerIcon />
      {inner ? (
        <p className="text-sm font-medium">Due date has passed</p>
      ) : (
        <p className="text-sm font-medium">
          You cannot submit this assignment because it has passed the due date
        </p>
      )}
    </div>
  );
};

const GradedRender = () => {
  return (
    <div className="flex items-center gap-2 p-3 mt-6 text-red-500 rounded-lg bg-red-50">
      <DangerIcon />
      <p className="text-sm font-medium">Assignment has been graded</p>
    </div>
  );
};
export default AssignmentSubmitArea;
